import { inquiryColumns, InquiryListRow } from './columns';
import {
  InquiryVersionSlot,
  InquiryWithContext,
} from '@texas/api/endpoints/inquiryApi';
import { VerifyDialogWithFunction } from '@texas/components/shared/dialog/VerifyDialogWithFunction';
import { DataTableContainer } from '@texas/components/shared/dataTable/DataTableContainer';
import { useMetadata } from '@texas/hooks/metadata/useMetadata';
import { useValueDisclosure } from '@texas/hooks/useValueDisclosure';
import { reactEvents } from '@bridge/reactEvents';
import { SetStateAction, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

interface Props {
  mode: 'matrix' | 'non-matrix';
  slots: InquiryVersionSlot[];
  inquiries: InquiryWithContext[];
  tierColumns: number[];
  displayCurrencyId: number | null;
  onCreateInquiry: (compositionGroupPurchaseInfoId: number) => Promise<void>;
}

export function InquiryMasterTable({
  mode,
  slots,
  inquiries,
  tierColumns,
  displayCurrencyId,
  onCreateInquiry,
}: Props) {
  const { t } = useTranslation();
  const activateDialog = useValueDisclosure<number>();
  const {
    useCurrenciesData: { convertCurrencyValue, findCurrency },
    currenciesData: [, refetchCurrencies],
  } = useMetadata();

  useEffect(() => {
    refetchCurrencies();
  }, [refetchCurrencies]);

  const rows = useMemo<InquiryListRow[]>(
    () =>
      slots.flatMap((slot): InquiryListRow[] => {
        const slotInquiries = inquiries.filter(
          (inquiry) =>
            inquiry.compositionGroupPurchaseInfoId ===
            slot.compositionGroupPurchaseInfoId,
        );
        return slotInquiries.length > 0
          ? slotInquiries.map((inquiry) => ({ inquiry, slot }))
          : [{ inquiry: null, slot }];
      }),
    [inquiries, slots],
  );

  const columns = useMemo(
    () =>
      inquiryColumns({
        t,
        mode,
        tierColumns,
        displayCurrencyId,
        convertCurrencyValue,
        findCurrency,
        onActivateInquiry: activateDialog.onOpen,
      }),
    [
      activateDialog.onOpen,
      convertCurrencyValue,
      displayCurrencyId,
      findCurrency,
      mode,
      t,
      tierColumns,
    ],
  );

  function handleRowClick(value: SetStateAction<number>) {
    const inquiryId = typeof value === 'function' ? value(NaN) : value;
    const row = rows.find((item) => item.inquiry?.id === inquiryId);
    if (!row?.inquiry) return;
    reactEvents.routingChanged.dispatch({
      to: 'variant.inquiry.detail',
      navObj: {
        compositionGroupId: row.slot.compositionGroupId,
        inquiryId: row.inquiry.id,
      },
    });
  }

  return (
    <>
      <DataTableContainer
        bg="entityDirectory.surface"
        border="1px solid"
        borderColor="entityDirectory.border"
        borderRadius={0}
        error={null}
        showColumnToggle={false}
        w="100%"
        datatable={{
          columns,
          data: rows,
          rowProps: {
            getRowId: (row) =>
              String(
                row.inquiry?.id ?? -row.slot.compositionGroupPurchaseInfoId,
              ),
            onRowClick: handleRowClick,
            uniqueRowStyle: (row) => (row.inquiry ? null : { opacity: 0.5 }),
          },
          variant: 'entity-directory',
        }}
      />
      <VerifyDialogWithFunction
        headerTitle={t('inquiry.activeInquiry')}
        isOpen={activateDialog.isOpen}
        onClose={activateDialog.onClose}
        primaryButtonTitle={t('general.confirm')}
        secondaryButtonTitle={t('general.cancel')}
        toPerform={() => {
          if (activateDialog.value) {
            onCreateInquiry(activateDialog.value);
          }
        }}
      >
        {t('inquiry.activeInquiryConfirm')}
      </VerifyDialogWithFunction>
    </>
  );
}
